/**
 * Waiting Room Service
 * 
 * Service for managing WebSocket connections, WebRTC setup, and participant management
 * in the waiting room
 */

import { ServerWebSocketService, WebSocketMessage } from './serverWebSocketService';
import { webRTCService, ICECandidateEvent } from './webrtcService';
import { roomStore } from '../stores/roomStore';
import { appLocalStore } from '../stores/appLocalStore';

export class SignallingService {
  private serverWebSocketService: ServerWebSocketService | null = null;
  private pendingIceCandidates: Map<string, RTCIceCandidateInit[]> = new Map();

  constructor() {
    this.setupWebRTC();
  }

  /**
   * Initialize the service and connect to WebSocket
   */
  initialize() {
    if (!roomStore.roomId || !roomStore.participantId) {
      throw new Error('Room ID and Participant ID are required');
    }

    // Setup WebSocket
    this.serverWebSocketService = new ServerWebSocketService({
      onMessage: this.handleWebSocketMessage.bind(this),
      // onOpen: this.handleWebSocketOpen.bind(this),
      onClose: this.handleWebSocketClose.bind(this),
    });
    
    // Connect to the WebSocket server
    this.serverWebSocketService.connect();
  }

  /**
   * Setup WebRTC event handlers
   */
  private setupWebRTC(): void {
    // Handle ICE candidates
    webRTCService.onICECandidate((event: ICECandidateEvent) => {
      if (this.serverWebSocketService && this.serverWebSocketService.isOpen()) {
        this.serverWebSocketService.send({
          type: 'ice-candidate',
          from: roomStore.participantId!,
          to: event.peerId,
          data: event.candidate,
        });
      } else {
        // Store for later if WebSocket isn't ready
        if (!this.pendingIceCandidates.has(event.peerId)) {
          this.pendingIceCandidates.set(event.peerId, []);
        }
        this.pendingIceCandidates.get(event.peerId)!.push(event.candidate);
      }
    });
  }

  // /**
  //  * Handle WebSocket open event
  //  */
  // private async handleWebSocketOpen(): Promise<void> {
  //   // Fetch current participants
  //   await this.fetchParticipants();
  // }

  /**
   * Handle WebSocket close event
   */
  private handleWebSocketClose(): void {
    if (this.serverWebSocketService) {
      this.serverWebSocketService.reconnect((attempts) => {
        appLocalStore.addErrorAction(`Connection lost. Failed to reconnect after ${attempts} attempts.`);
      });
    }
  }

  /**
   * Handle WebSocket messages
   */
  private async handleWebSocketMessage(data: WebSocketMessage): Promise<void> {
    switch (data.type) {
      case 'participant-joined':
        // this.participants = data.participants || [];
        // roomStore.setParticipants(this.participants);
        
        // If we are the host, initiate WebRTC connection to the new participant
        if (appLocalStore.isHost && data.participantId && roomStore.participantId !== data.participantId) {
          await this.initiateConnectionToParticipant(data.participantId);
        }
        break;
      
      case 'participant-left':
        // // Update participants list if provided, otherwise keep current list
        // if (data.participants) {
        //   this.participants = data.participants;
        //   roomStore.setParticipants(this.participants);
        // }
        
        // // Close the WebRTC connection to the departed participant
        // if (data.participantId) {
        //   webRTCService.closePeer(data.participantId);
        // }
        break;
      
      case 'offer':
        // Participant receives an offer from the host
        if (data.from && data.data) {
          await this.handleOffer(data.from, data.data);
        }
        break;
      
      case 'answer':
        // Host receives an answer from a participant
        if (data.from && data.data) {
          await this.handleAnswer(data.from, data.data);
        }
        break;
      
      case 'ice-candidate':
        // Handle ICE candidate from peer
        if (data.from && data.data) {
          await this.handleIceCandidate(data.from, data.data);
        }
        break;
      
      default:
        console.log('Unknown message type:', data.type);
    }
  }

  // /**
  //  * Fetch participants from the server
  //  */
  // private async fetchParticipants(): Promise<void> {
  //   const data = await roomService.fetchParticipants(roomStore.roomId!);
  //   if (data.success && data.participants) {
  //     this.participants = data.participants;
  //     roomStore.setParticipants(this.participants);
  //   }
  // }

  /**
   * Initiate WebRTC connections to new participants (host only)
   */
  private async initiateConnectionToParticipant(participantId: string): Promise<void> {
    if (!this.serverWebSocketService) return;

    // // Find participants we haven't connected to yet
    // const connectedPeerIds = webRTCService.getPeerIds();
    // const newParticipants = currentParticipants.filter(
    //   p => !p.isHost && !connectedPeerIds.includes(p.id)
    // );

    // for (const participant of newParticipants) {
      try {
        const offer = await webRTCService.createOffer(participantId);

        // Send the offer to the participant
        this.serverWebSocketService.send({
          type: 'offer',
          from: roomStore.participantId!,
          to: participantId,
          data: offer,
        });

        console.log('Sent offer to participant:', participantId);
      } catch (error) {
        console.error('Error creating offer for participant:', participantId, error);
      }
    // }
  }

  /**
   * Handle incoming offer
   */
  private async handleOffer(from: string, offer: RTCSessionDescriptionInit): Promise<void> {
    try {
      const answer = await webRTCService.handleOffer(from, offer);
      
      // Send the answer back to the host
      if (this.serverWebSocketService) {
        this.serverWebSocketService.send({
          type: 'answer',
          from: roomStore.participantId!,
          to: from,
          data: answer,
        });

        // Send any pending ICE candidates
        const pending = this.pendingIceCandidates.get(from) || [];
        for (const candidate of pending) {
          this.serverWebSocketService.send({
            type: 'ice-candidate',
            from: roomStore.participantId!,
            to: from,
            data: candidate,
          });
        }
        this.pendingIceCandidates.delete(from);
      }
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  }

  /**
   * Handle incoming answer
   */
  private async handleAnswer(from: string, answer: RTCSessionDescriptionInit): Promise<void> {
    try {
      await webRTCService.handleAnswer(from, answer);
    } catch (error) {
      console.error('Error processing answer:', error);
    }
  }

  /**
   * Handle incoming ICE candidate
   */
  private async handleIceCandidate(from: string, candidate: RTCIceCandidateInit): Promise<void> {
    try {
      await webRTCService.handleIceCandidate(from, candidate);
    } catch (error) {
      console.error('Error handling ICE candidate:', error);
    }
  }

  // /**
  //  * Update debug panel with connection info
  //  */
  // private updateDebugPanel(): void {
  //   const connectedPeerIds = webRTCService.getPeerIds();
  //   const debugConnections: PeerConnectionInfo[] = connectedPeerIds.map(peerId => {
  //     const participant = this.participants.find(p => p.id === peerId);
  //     const debugInfo = this.peerDebugInfo.get(peerId) || { lastSent: null, lastReceived: null };
      
  //     return {
  //       id: peerId,
  //       playerName: participant?.name || 'Unknown',
  //       lastSent: debugInfo.lastSent,
  //       lastReceived: debugInfo.lastReceived,
  //     };
  //   });

  //   debugPanelStore.updateConnections(debugConnections);
  // }
}

export const signallingService = new SignallingService();